import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Theme } from "@mui/material";

import { CustomerMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";

interface TocEntry {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  /** The manual Markdown (used to re-scan when content changes). */
  content: string;
  /** The scrolling content element that holds the rendered headings. */
  scrollContainer?: HTMLElement | null;
}

const titleSx = (theme: Theme) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  color: theme.palette.text.disabled,
  mb: 1.5
});

const itemSx = (level: number, active: boolean) => (theme: Theme) => ({
  padding: "4px 8px",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: level === 3 ? 12 : 13,
  pl: level === 3 ? 3 : 1,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontWeight: active || level === 2 ? 600 : 400,
  color: active
    ? theme.palette.primary.main
    : level === 2
      ? theme.palette.text.primary
      : theme.palette.text.secondary,
  backgroundColor: active ? theme.palette.action.selected : "transparent",
  "&:hover": { backgroundColor: theme.palette.action.hover }
});

/**
 * Renders a clickable, scroll-spy table of contents from the headings inside
 * the rendered manual. Mirrors the manual's `h2`/`h3` (rendered as `h5`/`h6`).
 */
const TableOfContents: React.FC<TableOfContentsProps> = ({ content, scrollContainer }) => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string>("");
  const [entries, setEntries] = useState<TocEntry[]>([]);

  useEffect(() => {
    if (!scrollContainer) return undefined;

    const timeout = setTimeout(() => {
      const headings = scrollContainer.querySelectorAll("h5[id], h6[id]");
      const tocEntries: TocEntry[] = Array.from(headings).map((heading) => ({
        id: heading.id,
        text: heading.textContent || "",
        level: heading.tagName.toLowerCase() === "h5" ? 2 : 3
      }));

      setEntries(tocEntries);
    }, 100);

    return () => clearTimeout(timeout);
  }, [scrollContainer, content]);

  const handleClick = useCallback((id: string) => {
    if (!scrollContainer) return;

    const element = scrollContainer.querySelector(`#${CSS.escape(id)}`);

    if (element) {
      const containerTop = scrollContainer.getBoundingClientRect().top;
      const elementTop = element.getBoundingClientRect().top;
      const offset = elementTop - containerTop + scrollContainer.scrollTop - 16;

      scrollContainer.scrollTo({ top: offset, behavior: "smooth" });
    }
  }, [scrollContainer]);

  useEffect(() => {
    if (!scrollContainer) return undefined;

    const observer = new IntersectionObserver(
      (observerEntries) => {
        observerEntries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { root: scrollContainer, rootMargin: "-20% 0px -70% 0px" }
    );

    const headings = scrollContainer.querySelectorAll("h5[id], h6[id]");

    headings.forEach((heading) => observer.observe(heading));

    return () => observer.disconnect();
  }, [scrollContainer, content]);

  if (entries.length === 0) return null;

  return (
    <div data-testid="toc">
      <Typography sx={titleSx}>{t(CustomerMessages.docsIndexTitle)}</Typography>
      {entries.map((entry) => (
        <Box
          key={entry.id}
          sx={itemSx(entry.level, activeId === entry.id)}
          onClick={() => handleClick(entry.id)}
          title={entry.text}
        >
          {entry.text}
        </Box>
      ))}
    </div>
  );
};

TableOfContents.defaultProps = {
  scrollContainer: null
};

export default TableOfContents;
